import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { apiClient } from "@/api/client";
import type { paths } from "@/types/api";

type CreateBody =
  paths["/api/v1/users"]["post"]["requestBody"]["content"]["application/json"];

const usersKey = ["users"] as const;

export function useUsers() {
  return useQuery({
    queryKey: usersKey,
    queryFn: async () => {
      const { data, error } = await apiClient.GET("/api/v1/users", {});
      if (error || !data) throw new Error("Failed to load team");
      return data;
    },
  });
}

export function useRoles() {
  return useQuery({
    queryKey: ["roles"],
    queryFn: async () => {
      const { data, error } = await apiClient.GET("/api/v1/authorization/roles", {});
      if (error || !data) throw new Error("Failed to load roles");
      return data;
    },
  });
}

export function useCreateUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: CreateBody) => {
      const { data, error, response } = await apiClient.POST("/api/v1/users", { body });
      if (error || !data) {
        const detail = (error as { detail?: string } | undefined)?.detail;
        throw new Error(detail ?? `Could not add member (${response.status})`);
      }
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: usersKey }),
  });
}

export function useSetUserActive() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { id: string; is_active: boolean }) => {
      const { data, error } = await apiClient.PATCH("/api/v1/users/{user_id}", {
        params: { path: { user_id: vars.id } },
        body: { is_active: vars.is_active },
      });
      if (error || !data) throw new Error("Could not update member");
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: usersKey }),
  });
}

export function useAssignRole() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { id: string; role_code: string }) => {
      const { data, error } = await apiClient.POST("/api/v1/users/{user_id}/roles", {
        params: { path: { user_id: vars.id } },
        body: { role_code: vars.role_code },
      });
      if (error || !data) throw new Error("Could not assign role");
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: usersKey }),
  });
}
